import axios from 'axios';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import { TableTitle } from '../commonComponent/tableElement/TableTitle';
import {
  encodeSearchWord,
  shortenSchoolName,
  timestamp2Localestring,
} from '../../common/helper';
import { KEY_SCHOOL_CLASS_LIST, KEY_SCHOOL_LIST } from '../../common/key';
import {
  deleteSchoolClass,
  getSchoolClassList,
  getSchoolList,
  registerSchoolClass,
} from '../../common/api/ApiSchool';
import excellog from '../../images/sub/excel_icon.svg';
import { Footer } from '../commonComponent/views/Footer';
import { SwSchoolClass } from '../commonComponent/SwSchoolClass';
import { schoolListState } from '../../common/atom';
import { RESULT_ALREADY_REGISTER, RESULT_OK } from '../../common/resultCode';
import { SwAlert } from '../commonComponent/views/SwAlert';

const columns = [
  '교육지원청',
  '학교',
  '학년',
  '반',
  '읽기 목표',
  '걷기 목표',
  '학급 초대코드',
];

interface Props {}

export const SchoolInquire: React.FC<Props> = () => {
  const queryClient = useQueryClient();
  const formInputRef = useRef<any>();
  const { loginVal } = useSession();
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [resultFilter, setResultFilter] = useState<string>('');
  const [requestList, setRequestList] = useState<any>([]);
  const [schoolClassList, setSchoolClassList] = useState<any>([]);
  const [isAdd, setIsAdd] = useState<boolean>(true);
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState<string>('school');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dailyStatType, setDailyStatType] = useState<any>(0);
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openSchoolClass, setOpenSchoolClass] = useState<boolean>(false);
  const [hoveredRow, setHoveredRow] = useState<number>(-1);

  const {
    data,
    isLoading,
    refetch: schoolClassRefetch,
  } = useQuery(
    KEY_SCHOOL_CLASS_LIST,
    () => {
      const newData = {
        page: page,
        rowsPerPage: rowsPerPage,
        searchKey: 'schoolclass',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      if (reqRequest) return getSchoolClassList(newData);
      return null;
    },
    {
      onSuccess: res => {
        if (res) {
          const newSchoolClassInfo =
            res.schoolList &&
            res.schoolList.map((item: any, idx: number) => {
              const newData = [
                item.educationOfficeName,
                item.schoolName,
                item.schoolGrade,
                item.schoolClass,
                item.readGoal,
                item.walkGoal,
                `${shortenSchoolName(item.schoolName)}_G${item.schoolGrade}C${
                  item.schoolClass
                }`,
              ];
              return newData;
            });
          setRequestList(newSchoolClassInfo);
          setSchoolClassList(res.schoolList);
          setTotalRecords(res.totalRecordCount);
        }
        setLoading(false);
      },
      onError: e => {
        console.log(e);
        setLoading(false);
      },
    }
  );

  const resultPrefetch = (newPage: number) => {
    queryClient.prefetchQuery(KEY_SCHOOL_CLASS_LIST, () => {
      const newData = {
        page: newPage,
        rowsPerPage: rowsPerPage,
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      getSchoolClassList(newData);
    });
  };

  const downloadSchoolClassList = (param: any) =>
    xapi
      .get(
        `/admin/v1/apis/school/download/school-class?searchKey=${param.searchKey}&searchWord=${param.searchWord}&startDate=${param.startDate}&endDate=${param.endDate}`,
        { responseType: 'blob' }
      )
      .then(res => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `school_class.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout((_: any) => {
          window.URL.revokeObjectURL(url);
        }, 600000);
        a.remove();
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.error('err: ', err);
      });

  const downloadSchoolClass = () => {
    const newData = {
      searchKey: 'school-class',
      searchWord: resultFilter,
      startDate: 0,
      endDate: 0,
    };
    setLoading(true);
    downloadSchoolClassList(newData);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const searchSchool = () => {
    if (page === 1) {
      setLoading(true);
      schoolClassRefetch();
    } else {
      setPage(1);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      searchSchool();
    }
  };

  const downloadSampleExcel = () => {
    const url =
      'https://d15wsbb3qf2hxa.cloudfront.net/common/%ED%95%99%EA%B5%90_%EC%97%91%EC%85%80%EC%97%85%EB%A1%9C%EB%93%9C%EC%9A%A9_%EC%96%91%EC%8B%9D.xlsx';
    const a = document.createElement('a');
    a.href = url;
    a.download = `업로드양식.xlsx`;
    document.body.appendChild(a);
    a.click();
    setTimeout((_: any) => {
      window.URL.revokeObjectURL(url);
    }, 60000);
    a.remove();
  };

  const uploadExcelFile = (evt: ChangeEvent<HTMLInputElement>) => {
    const excelFile = evt.target.files && evt.target.files[0];
    var formData = new FormData();
    if (excelFile !== null) formData.append('file', excelFile);

    xapi.post(`/admin/v1/apis/school/upload/class-form`, formData).then(res => {
      formInputRef.current.value = null;
      if (res.status !== 200) throw new Error('http 에러');
      setOpenSnackbar(true);
      setDialogMessage('엑셀 업로드로 등록했습니다.');
      return res.data;
    });
  };

  const { mutate: registerSchoolClassMutate, isError: isMutateError } =
    useMutation(registerSchoolClass, {
      onSuccess: res => {
        if (res.resultCode === RESULT_OK) {
          setOpenSnackbar(true);
          setDialogMessage(
            isAdd ? '학교를 추가했습니다.' : '학교를 수정했습니다.'
          );
          schoolClassRefetch();
        } else if (res.resultCode === RESULT_ALREADY_REGISTER) {
          setOpenDialog(true);
          setDialogMessage(
            `이미 등록된 학교 정보입니다. 목록을 확인 후 다시 시도해주세요`
          );
        } else {
          setOpenDialog(true);
          setDialogMessage(
            `학교를 ${isAdd ? '추가' : '수정'}하는 동안 오류가 발생했습니다.(${
              res.resultMsg
            })`
          );
        }
      },
      onError: error => {
        setOpenSnackbar(true);
        setDialogMessage(
          `학교 ${
            isAdd ? '추가가' : '수정이'
          } 실패했습니다. 다시 시도해주시기 바랍니다.`
        );
      },
    });

  const saveSchoolInfo = (item: any) => {
    if (item.schoolSeq === 0) {
      setOpenSnackbar(true);
      setDialogMessage('학교를 선택해주세요');
      return;
    }
    const newData: any = {
      schoolClassSeq: item.schoolClassSeq,
      schoolSeq: item.schoolSeq,
      schoolGrade: item.schoolGrade,
      schoolClass: item.schoolClass,
      years: item.years,
      readGoal: item.readGoal,
      walkGoal: item.walkGoal,
      teacherCode: loginVal.value.user.id,
      isAdd: isAdd ? 1 : 0,
    };
    registerSchoolClassMutate(newData);
  };

  const { mutate: deleteSchoolClassMutate, isError: isDeleteMutateError } =
    useMutation(deleteSchoolClass, {
      onSuccess: res => {
        if (res.resultCode === RESULT_OK) {
          setDialogMessage(`학교/학년/반 정보를 삭제했습니다.`);
          schoolClassRefetch();
        } else {
          setDialogMessage(
            `학교/학년/반 정보를 삭제하는 동안 오류가 발생했습니다.(${res.resultCodeMsg})`
          );
        }
        setOpenSnackbar(true);
      },
      onError: error => {
        console.log(error);
        setDialogMessage(
          `학교/학년/반 정보를 삭제하는 동안 시스템 오류가 발생했습니다.`
        );
        setOpenSnackbar(true);
      },
    });

  const deleteSchoolInfo = (item: any) => {
    const newData: any = {
      schoolClassSeq: item.schoolClassSeq,
    };
    deleteSchoolClassMutate(newData);
  };

  const clickRow = (idx: number, opType: boolean) => {
    setSelectedRow(idx);
    setIsAdd(opType);
    setOpenSchoolClass(true);
  };

  useEffect(() => {
    setLoading(true);
    schoolClassRefetch();
  }, [page]);

  useEffect(() => {
    if (reqRequest) {
      schoolClassRefetch();
    }
  }, [reqRequest]);

  useEffect(() => {
    setReqRequest(true);
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <Box className='flex_between' sx={{ p: '1rem 0' }}>
          {(loginVal.value.user.roleName === '관리자2' ||
            loginVal.value.user.roleName === '마스터') && (
            <TextField
              sx={{ width: '24rem' }}
              placeholder='학교이름으로 검색'
              value={searchWord}
              onChange={evt => setSearchWord(evt.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton sx={{ pr: 0 }} onClick={searchSchool}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
        <Box
          className={
            loginVal.value.user.roleName === '마스터'
              ? 'flex_between'
              : 'flex_end'
          }
        >
          {loginVal.value.user.roleName === '마스터' && (
            <Box>
              <Button
                color='info'
                variant='outlined'
                onClick={() => {
                  clickRow(-1, true);
                }}
              >
                추가
              </Button>
              <Button
                sx={{ ml: '1rem' }}
                color='info'
                variant='outlined'
                onClick={() => downloadSampleExcel()}
              >
                양식 다운로드
                <img
                  src={excellog}
                  alt='icon'
                  style={{ marginLeft: 8 }} // Adjust margin as needed
                />
              </Button>
              <Button
                sx={{ ml: '1rem' }}
                color='info'
                variant='outlined'
                onClick={() => {
                  const schoolfile = document.getElementById(
                    'upload_school_form_excel'
                  );
                  schoolfile?.click();
                }}
              >
                양식 업로드
                <img
                  src={excellog}
                  alt='icon'
                  style={{ marginLeft: 8 }} // Adjust margin as needed
                />
              </Button>
              <input
                className='nonedisplay'
                id='upload_school_form_excel'
                type='file'
                ref={formInputRef}
                onChange={uploadExcelFile}
              />
            </Box>
          )}
          <Box>
            <Button
              sx={{ mr: '1rem' }}
              color='info'
              variant='outlined'
              onClick={() => {
                downloadSchoolClass();
              }}
            >
              다운로드
              <FileDownloadRoundedIcon color='success' />
            </Button>
          </Box>
        </Box>
        <Paper sx={{ p: '1rem' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column: any, idx: number) => (
                    <TableCell key={idx.toString()} sx={{ p: '.4rem .7rem' }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requestList.length > 0 &&
                  requestList.map((row: any, idx: number) => (
                    <TableRow
                      key={idx.toString()}
                      onClick={() => clickRow(idx, false)}
                      style={{
                        cursor: hoveredRow === idx ? 'pointer' : 'default',
                      }}
                      onMouseEnter={() => setHoveredRow(idx)}
                      onMouseLeave={() => setHoveredRow(-1)}
                    >
                      {columns.map((column: any, i: number) =>
                        i === 4 || i === 5 ? (
                          <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                            <span
                              style={{
                                color: 'blue',
                                textDecoration: 'underline',
                              }}
                            >
                              {row[i]}
                            </span>{' '}
                            <span
                              style={{
                                fontSize: '70%',
                              }}
                            >
                              ✏️
                            </span>
                          </TableCell>
                        ) : (
                          <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                            {row[i]}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <SwPagination
            page={page}
            handleChangePage={handleChangePage}
            count={Math.ceil(totalRecords / rowsPerPage)}
          />
        </Paper>
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwAlert
        open={openDialog}
        onConfirm={() => setOpenDialog(false)}
        contents={dialogMessage}
      />
      <SwSchoolClass
        open={openSchoolClass}
        onClose={() => setOpenSchoolClass(false)}
        isAdd={isAdd}
        schoolClassInfo={schoolClassList[selectedRow]}
        onSave={(item: any) => {
          saveSchoolInfo(item);
        }}
        onDelete={(item: any) => {
          deleteSchoolInfo(item);
        }}
      />
      {loading && <SwLoading />}
    </>
  );
};
